import {
  trapiClient,
  fetchAllPaginatedConfigs,
  MAX_ITEM_COUNT_REQUEST_HEADER,
  CONTINUATION_HEADER,
} from './trapiClient';
import type { Environment } from '../../contexts/AppContext';
import type { ModelApprovalRecord, ModelApprovalReviewPayload } from '../../types/trapi';

// Checkpoint df4e98f8 review finding ARCH-1: the generic opt-in /configs
// pagination contract (trapi-max-item-count/trapi-continuation/
// trapi-pagination), the IncompletePaginatedListError it can throw, and the
// fetchAllPaginatedConfigs() helper that implements it now live in
// trapiClient.ts, not here -- they have no MODEL_APPROVAL-specific logic and
// are reused by any future /configs-backed list that needs paging (e.g.
// ApisService.getApis, which has no page limit today either). This file is
// now just a caller.

// A bounded page size matching the service's documented maximum (see the
// trapi-admin skill). Sized for headroom, not tuned to any specific observed
// count, so growth in the number of approval records does not silently
// truncate the admin list. (For the DEV verification this dependency is
// based on, see the trapi-admin skill's Pagination section and this
// service's own commit history/PR description, not a bare inline claim here
// -- checkpoint df4e98f8 review finding ADV-5.)
const PAGE_SIZE = 1000;

/**
 * RAI Approved Models (ci=MODEL_APPROVAL) admin API client.
 *
 * List/Get reuse the existing generic /configs endpoints. The `ADMINCI`
 * partition is pinned via an explicit `trapi-partitionkey` request header on
 * every call (F2, PR6 review) rather than relying on get-configs.xml's
 * server-side default of ADMINCI -- so this client's ADMINCI-only intent is
 * never silently reinterpreted if that server-side default ever changes for
 * an unrelated reason (e.g. a future partition-scoping refactor). Create/
 * Delete are intentionally NOT exposed here: this service only covers the
 * scope approved for the admin UI (list + the sanctioned Review/Edit
 * action). The generic PUT /configs/{id} (full-document replace) is
 * deliberately never used for MODEL_APPROVAL records — it 400s server-side
 * (see PR1's update-config.xml guard) because it would silently drop
 * state/scope/approvedApiPaths. reviewModelApproval below is the only
 * sanctioned way to mutate an existing record.
 *
 * Pagination (F2, PR6 review, closed out against the live PR4 contract): GET
 * /configs (get-configs.xml) supports opt-in paging via
 * `trapi-max-item-count` / `trapi-continuation` / `trapi-pagination` --
 * getModelApprovals below always opts in (see trapiClient.ts's
 * fetchAllPaginatedConfigs) and follows every page until the server signals
 * completion, failing closed rather than ever silently truncating the list.
 *
 * IMPORTANT (checkpoint df4e98f8 review findings SKEP-1 / ADV-1): the exact
 * server-side get-configs.xml implementation of this contract is owned by a
 * separate, out-of-scope change (informally "PR4") and may not be reflected
 * verbatim in every branch of this repository at any given time -- this is a
 * real merge-order dependency, not merely a defensive guard against an
 * unlikely edge case.
 *
 * UPDATE (PR6 hosted-review finding, thread 971361): the original framing
 * above -- "deployed against a get-configs.xml that does not yet implement
 * this contract, getModelApprovals will deterministically fail closed on
 * every call" -- was itself the bug this finding identified: it meant this
 * admin page would show an error and zero records on EVERY load against
 * today's get-configs.xml, well before "PR4" merges, with nothing gating
 * that rollout dependency. fetchAllPaginatedConfigs now recognizes that
 * specific shape (capability header absent on the first page, no
 * continuation token offered) as "the server never opted into this
 * contract, and a bare 200 array from a cross-partition query without an
 * explicit page size already IS the complete list" and returns it directly
 * instead of throwing -- see trapiClient.ts's file-level comment above the
 * pagination contract's constants for the exact, narrow conditions. Once
 * "PR4"'s contract is live, responses gain `trapi-pagination: supported`
 * and this codepath is never reached; nothing else about the opt-in
 * request shape below needs to change when that happens.
 */
export class ModelApprovalsService {
  private static readonly BASE_PATH = '/configs';
  private static readonly REVIEW_BASE_PATH = '/admin/model-approvals';

  static async getModelApprovals(params: {
    environment: Environment;
    accessToken: string;
  }): Promise<ModelApprovalRecord[]> {
    const { environment, accessToken } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const url = `${this.BASE_PATH}?ci=MODEL_APPROVAL`;

    return fetchAllPaginatedConfigs<ModelApprovalRecord>(
      (continuationToken, pageSize) => {
        const headers: Record<string, string> = {
          'trapi-partitionkey': 'ADMINCI',
          [MAX_ITEM_COUNT_REQUEST_HEADER]: String(pageSize),
        };
        if (continuationToken) {
          headers[CONTINUATION_HEADER] = continuationToken;
        }
        return trapiClient.getWithHeaders<unknown>(url, accessToken, headers);
      },
      'model approval',
      PAGE_SIZE,
    );
  }

  static async getModelApproval(params: {
    id: string;
    environment: Environment;
    accessToken: string;
  }): Promise<ModelApprovalRecord> {
    const { id, environment, accessToken } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const url = `${this.BASE_PATH}/${encodeURIComponent(id)}`;
    const { data } = await trapiClient.getWithHeaders<ModelApprovalRecord>(
      url,
      accessToken,
      { 'trapi-partitionkey': 'ADMINCI' },
    );
    return data;
  }

  /**
   * The sanctioned way to change state (approve/reject/under_review) and/or
   * correct modelName/notes/modelProvider on an existing record. Never call
   * the generic PUT /configs/{id} for MODEL_APPROVAL — see class doc above.
   */
  static async reviewModelApproval(params: {
    id: string;
    environment: Environment;
    accessToken: string;
    payload: ModelApprovalReviewPayload;
  }): Promise<ModelApprovalRecord> {
    const { id, environment, accessToken, payload } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const url = `${this.REVIEW_BASE_PATH}/${encodeURIComponent(id)}/review`;
    return trapiClient.post<ModelApprovalRecord>(url, payload, accessToken);
  }
}
